import React, { useState } from 'react';
import { Card, Typography, Divider, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import FormTitle from '../../../FormFields/FormTitle';
import FormMoneyField from '../../../FormFields/FormMoneyField';
import FormNumberField from '../../../FormFields/FormNumberField';
import FormTextField from '../../../FormFields/FormTextField';
import FormAutocomplete from '../../../FormFields/FormAutocomplete';
import FormVendorField from '../../../FormFields/FormVendorField';
import FieldContainer from '../../../FormFields/FieldContainer';
import FormStaffInfoFieldWithContracted from '../../../FormFields/StaffInfoField/FormStaffInfoFieldWithContracted';
import { formCardStyle } from '../../../TransitionStyles';
import { DIETARY } from '../../../formQuestionsEnum';
import { FILE_TYPES } from '../../../formFileTypesEnum';

DieticianForm.propTypes = {
  permissions: PropTypes.object,
};

export default function DieticianForm({ permissions }) {
  const [focusedField, setFocusedField] = useState(null);
  const { watch } = useFormContext();
  const contractedRateTypesList = useSelector((state) => state.transitions?.data?.contractedRateTypesList);

  const isContracted = watch('dietician')?.contracted;

  const fieldIds = [
    DIETARY.DIETICIAN.id,
    DIETARY.OUTSOURCED_DIETICIAN.id,
    DIETARY.OUTSOURCED_DIETICIAN_CONTACT.id,
    DIETARY.OUTSOURCED_DIETICIAN_HOURS_PER_WEEK.id,
    DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_RATE.id,
    DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_PRICE.id,
  ];

  const showForm = fieldIds.some((id) => id in permissions);

  return (
    showForm && (
      <Card sx={formCardStyle}>
        <Typography sx={{ mb: 1 }} align="center" variant="body1" color="info.main">
          Dietician
        </Typography>
        <Divider sx={{ mb: 1, mt: 2, borderWidth: 1 }} />
        <Grid container spacing={1.5}>
          {DIETARY.DIETICIAN.id in permissions && (
            <Grid item xs={12}>
              <FormStaffInfoFieldWithContracted
                fieldName={DIETARY.DIETICIAN.fieldName}
                fieldAttributes={{
                  isEditable: permissions[DIETARY.DIETICIAN.id],
                  questionTitle: 'Current dietician',
                  fieldName: DIETARY.DIETICIAN.fieldName,
                }}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
              />
            </Grid>
          )}

          {isContracted && (
            <>
              {DIETARY.OUTSOURCED_DIETICIAN.id in permissions && (
                <Grid item xs={12}>
                  <FormTitle fieldAttributes={{ questionTitle: 'Outsourced dietician vendor', fieldType: 'title' }} />
                  <FormVendorField
                    fieldName={DIETARY.OUTSOURCED_DIETICIAN.fieldName}
                    fieldAttributes={{ isEditable: permissions[DIETARY.OUTSOURCED_DIETICIAN.id] }}
                    invoiceFileTypeId={FILE_TYPES.OUTSOURCED_DIETICIAN_VENDOR_INVOICE}
                    contractFileTypeId={FILE_TYPES.OUTSOURCED_DIETICIAN_VENDOR_CONTRACT}
                  />
                </Grid>
              )}

              {DIETARY.OUTSOURCED_DIETICIAN_CONTACT.id in permissions && (
                <Grid item xs={12}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Contact' }} />
                    <FormTextField
                      fieldName={DIETARY.OUTSOURCED_DIETICIAN_CONTACT.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.OUTSOURCED_DIETICIAN_CONTACT.id] }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.OUTSOURCED_DIETICIAN_HOURS_PER_WEEK.id in permissions && (
                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Hours per week' }} />
                    <FormNumberField
                      fieldName={DIETARY.OUTSOURCED_DIETICIAN_HOURS_PER_WEEK.fieldName}
                      fieldAttributes={{
                        isEditable: permissions[DIETARY.OUTSOURCED_DIETICIAN_HOURS_PER_WEEK.id],
                      }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                      allowDecimal
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_RATE.id in permissions && (
                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Contracted rate' }} />
                    <FormAutocomplete
                      fieldName={DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_RATE.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_RATE.id] }}
                      options={contractedRateTypesList}
                    />
                  </FieldContainer>
                </Grid>
              )}

              {DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_PRICE.id in permissions && (
                <Grid item xs={12} md={4}>
                  <FieldContainer>
                    <FormTitle fieldAttributes={{ questionTitle: 'Contracted price' }} />
                    <FormMoneyField
                      fieldName={DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_PRICE.fieldName}
                      fieldAttributes={{ isEditable: permissions[DIETARY.OUTSOURCED_DIETICIAN_CONTRACTED_PRICE.id] }}
                      focusedField={focusedField}
                      setFocusedField={setFocusedField}
                    />
                  </FieldContainer>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Card>
    )
  );
}
